
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { HexColorPicker } from "react-colorful";
import { cn } from "@/lib/utils/cn";

interface ColorPickerProps {
  value?: string | null;
  onChange: (color: string | undefined) => void;
  className?: string;
  allowClear?: boolean;
}

/**
 * ColorPicker Component
 * 
 * A visual color picker with hex input for selecting colors.
 * Uses react-colorful for the color picker UI.
 * 
 * @param value - Current color in hex format (#RRGGBB)
 * @param onChange - Callback when color changes
 */
export function ColorPicker({ value, onChange, className, allowClear = true }: ColorPickerProps) {
  const [open, setOpen] = useState(false);

  // Ensure value is always a valid hex color for display
  const normalizedValue = value?.match(/^#[0-9A-Fa-f]{6}$/) ? value : '#000000';
  const hasValue = !!value;

  const handleHexInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    let hex = e.target.value;
    
    // Add # if missing
    if (!hex.startsWith('#')) {
      hex = '#' + hex;
    }

    // Validate hex format
    if (/^#[0-9A-Fa-f]{0,6}$/.test(hex)) {
      if (hex.length === 7) {
        onChange(hex.toUpperCase());
      }
    }
  };

  const handleClear = () => {
    onChange(undefined);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "lw-color-picker-trigger flex items-center gap-2 w-full h-9 px-3 rounded-md border border-input bg-background hover:bg-accent transition-colors",
            className
          )}
        >
          <div
            className="w-6 h-6 rounded border border-border flex-shrink-0"
            style={{
              backgroundColor: hasValue ? normalizedValue : 'transparent',
              backgroundImage: hasValue ? 'none' : 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)',
              backgroundSize: hasValue ? 'auto' : '8px 8px',
              backgroundPosition: hasValue ? 'center' : '0 0, 0 4px, 4px -4px, -4px 0px',
            }}
            aria-label="Current color"
          />
          <span className="text-sm font-mono flex-1 text-left">
            {hasValue ? normalizedValue : 'None'}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3" align="start">
        <div className="space-y-3">
          <HexColorPicker color={normalizedValue} onChange={onChange} />
          <Input
            type="text"
            value={normalizedValue}
            onChange={handleHexInput}
            placeholder="#000000"
            className="font-mono text-sm"
            maxLength={7}
          />
          {allowClear && hasValue && (
            <button
              type="button"
              onClick={handleClear}
              className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors py-1"
            >
              Clear Color
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
